/**
 * Shared state for the "Go to a world" panel (2D and VR): the world list,
 * search, the chosen destination, who in this room can be invited, and the
 * invite / go actions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorldStore } from '../../stores/worldStore';
import { getPresenceKey } from '../../services/realtimeManager';
import {
  canInvite, loadHopWorlds, sendWorldInvites, travelToWorld,
  type HopWorld, type InviteResult, type InviteTarget,
} from '../../services/worldHop';

export interface HopPerson extends InviteTarget {
  /** Why they can't be invited to the chosen world, if they can't. */
  blockedReason?: string;
}

export function useWorldHopPicker(roomWorldId: string | undefined, open: boolean) {
  const [worlds, setWorlds] = useState<HopWorld[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<HopWorld | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, InviteResult>>({});
  const signedIn = useWorldStore((s) => !!s.user?.id);
  const myUserId = useWorldStore((s) => s.user?.id || null);
  const online = useWorldStore((s) => s.onlinePlayers);

  // Load (or refresh) the list each time the panel opens.
  useEffect(() => {
    if (!open) return;
    let live = true;
    setError(null);
    loadHopWorlds(roomWorldId).then((w) => { if (live) setWorlds(w); })
      .catch(() => { if (live) { setWorlds([]); setError('Couldn’t load worlds. Check your connection.'); } });
    return () => { live = false; };
  }, [open, roomWorldId]);

  // Fresh start each time it opens.
  useEffect(() => {
    if (open) return;
    setSelected(null); setPicked(new Set()); setStatus(null); setResults({}); setQuery('');
  }, [open]);

  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => (worlds || []).filter((w) => !q || w.name.toLowerCase().includes(q) || (w.ownerName || '').toLowerCase().includes(q)),
    [worlds, q],
  );

  // Everyone else in this room (not you, not your other tabs).
  const myKey = getPresenceKey();
  const people: HopPerson[] = useMemo(() => online
    .filter((p) => p.id !== myKey && !(p.userId && p.userId === myUserId))
    .filter((p) => canInvite(p.userId, p.id))
    .map((p) => ({
      key: p.id, userId: p.userId || null, userName: p.userName || 'Player',
      blockedReason: !p.userId && selected && !selected.isPublic ? 'Guest — private world' : undefined,
    })), [online, myKey, myUserId, selected]);

  const choose = useCallback((w: HopWorld | null) => {
    setSelected(w); setStatus(null); setResults({}); setPicked(new Set());
  }, []);

  const toggle = useCallback((key: string) => {
    setPicked((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }, []);

  /** Why inviting to the chosen world isn't possible right now (null = it is). */
  const inviteBlock = !selected ? null
    : !signedIn ? 'Sign in to invite people.'
    : !selected.canShare ? 'This world is private. Ask the owner to invite them.'
    : people.length === 0 ? 'Nobody else is here right now.'
    : null;

  const invite = useCallback(async (everyone = false) => {
    if (!selected || !roomWorldId || busy) return;
    const targets = people.filter((p) => !p.blockedReason && (everyone || picked.has(p.key)));
    if (!targets.length) { setStatus(everyone ? 'Nobody here can be invited there.' : 'Choose who to invite.'); return; }
    setBusy(true); setStatus(null);
    try {
      const res = await sendWorldInvites(roomWorldId, selected, targets);
      const map: Record<string, InviteResult> = {};
      for (const r of res) map[r.key] = r;
      setResults((prev) => ({ ...prev, ...map }));
      const ok = res.filter((r) => r.ok).length;
      setStatus(ok ? `Invite sent to ${ok === 1 ? res.find((r) => r.ok)!.userName : `${ok} people`}.` : 'No invites were sent.');
      setPicked(new Set());
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Couldn’t send invites.');
    } finally { setBusy(false); }
  }, [selected, roomWorldId, busy, people, picked]);

  const go = useCallback(() => {
    if (selected) void travelToWorld(selected.id, selected.name);
  }, [selected]);

  return {
    worlds, error, query, setQuery, shown, selected, choose, people, picked, toggle,
    invite, go, busy, status, results, inviteBlock, signedIn,
  };
}
